// sections/VoiceChannelSection.jsx
// AI Voice Channel management — phone number, voice config, greeting, behavior, campaign.
// Modelled directly on WebChatSection.jsx: same auth helpers, same SectionCard,
// same dirty-tracking + parallel-PATCH save pattern, same banner + button styles.

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone,
  PhoneCall,
  Mic,
  Sliders,
  Zap,
  Globe,
  Tag,
  Copy,
  Check,
  Loader2,
  AlertCircle,
  CheckCircle,
  Info,
  Repeat,
  Search,
  X,
} from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';
import supabase from '../../../../lib/supabaseClient';

// ============================================================================
// HELPERS — verbatim from WebChatSection.jsx
// ============================================================================

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (process.env.NODE_ENV === 'development') return 'http://localhost:3001';
  return 'https://api.getsurfox.com/api';
};

const makeAuthenticatedRequest = async (endpoint, options = {}) => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw new Error(`Authentication error: ${sessionError.message}`);
  if (!session?.access_token) throw new Error('No valid session. Please log in again.');

  const response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    let msg = `${response.status} ${response.statusText}`;
    try { const e = await response.json(); msg = e.error || e.message || msg; } catch {}
    throw new Error(msg);
  }

  const text = await response.text();
  return text ? JSON.parse(text) : {};
};

// Curated voice list — Retell supports many more. Operators can type a custom
// id in the "Custom" slot to use anything from the Retell voice library.
//
// BUG FOUND 2026-09-14 (this is the real, complete root cause of the whole
// "update-agent/create-agent 404" Phase 2 investigation — confirmed by Retell
// support, then independently verified against their real /list-voices
// catalog and by repeated live create/update calls): 'retell-Anna' and
// 'retell-Adrian' never existed at all, and 'eleven-labs-Adrian' used the
// wrong id format (real one is '11labs-Adrian'). Only 'retell-Cimo' was ever
// a real voice. Every 404 chased across this whole rollout was this — not a
// Retell platform bug, not client-side flakiness (a same-night investigation
// wrongly concluded flakiness by comparing successful retell-Cimo calls
// against failed retell-Anna calls as if they were repeats of the same test).
// Replacements below are real platform-tier (Retell-native, no extra
// per-minute cost) voices, picked to keep the "not the paid ElevenLabs one"
// framing intact — labels renamed to match their real names so this can
// never drift silently again.
const VOICE_PRESETS = [
  { id: 'retell-Cimo',     label: 'Cimo (natural female, default)' },
  { id: 'retell-Marissa',  label: 'Marissa (warm female)' },
  { id: 'retell-Nico',     label: 'Nico (confident male)' },
  { id: '11labs-Adrian',   label: 'Adrian — ElevenLabs (premium)' },
];

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const SectionCard = ({ icon: Icon, iconColor, title, subtitle, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <div className="flex items-center space-x-3 mb-5">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ${iconColor}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <h4 className="font-semibold text-slate-900">{title}</h4>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                 text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors flex-shrink-0"
    >
      {copied ? (
        <><Check className="w-3.5 h-3.5 text-emerald-600" /><span className="text-emerald-600">Copied</span></>
      ) : (
        <><Copy className="w-3.5 h-3.5" /><span>Copy</span></>
      )}
    </button>
  );
};

// Format an E.164 number for display: +14155551234 → +1 (415) 555-1234
const formatPhone = (e164) => {
  if (!e164) return '';
  const digits = e164.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return e164;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function VoiceChannelSection({ onUpdate }) {
  const { user } = useAuth();

  const [loading, setLoading]                 = useState(true);
  const [saving, setSaving]                   = useState(false);
  const [provisionLoading, setProvisionLoading] = useState(false);
  const [importLoading, setImportLoading]     = useState(false);
  const [error, setError]                     = useState('');
  const [success, setSuccess]                 = useState('');

  // Phone Number state (read-only from server — provisioning/swap are separate routes)
  const [voiceAiEnabled, setVoiceAiEnabled]       = useState(false);
  const [provisionedAt, setProvisionedAt]         = useState(null);
  const [areaCodeInput, setAreaCodeInput]         = useState('');
  // 🟢 2026-09-13 (Voice Channel Settings redesign): the displayed number is the
  // DESIGNATED VOICE CAMPAIGN's own Retell-imported number — never
  // tenants.retell_phone_number (the old tenant-level dedicated-line field).
  // See _shared/retellCampaignNumbers.ts's header for why. null when no Voice
  // campaign is designated at all.
  const [voiceCampaign, setVoiceCampaign] = useState(null);
  // phone_number is null once we're campaign-sourced but that campaign has no
  // number imported yet (shouldn't happen for a Surf-created Voice campaign —
  // VOICE_CAMPAIGN_NEEDS_NUMBER — but can for one designated manually via the
  // Configuration card's campaign picker, or before this tenant is provisioned).
  const phoneNumber = voiceCampaign?.retell_imported_at ? voiceCampaign.phone_number : '';

  // Swap Number flow
  const [swapModalOpen, setSwapModalOpen]     = useState(false);
  const [swapping, setSwapping]               = useState(false);
  const [swapMode, setSwapMode]               = useState('existing'); // 'existing' | 'search'
  const [inventoryNumbers, setInventoryNumbers] = useState([]);
  const [selectedInventoryId, setSelectedInventoryId] = useState('');
  const [swapAreaCode, setSwapAreaCode]       = useState('');
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [purchasing, setPurchasing]           = useState(false);
  const [swapError, setSwapError]             = useState('');

  // Voice & Greeting state
  const [voiceId, setVoiceId]                     = useState('retell-Cimo');
  const [customVoice, setCustomVoice]             = useState('');
  const [isCustomVoice, setIsCustomVoice]         = useState(false);
  const [voiceSpeed, setVoiceSpeed]               = useState(1.0);
  const [voiceTemperature, setVoiceTemperature]   = useState(0.5);
  const [greeting, setGreeting]                   = useState('');
  const [endMessage, setEndMessage]               = useState('');

  // Behavior state
  const [afterHoursEnabled, setAfterHoursEnabled] = useState(false);
  const [afterHoursMessage, setAfterHoursMessage] = useState('');
  const [maxCallDurationSec, setMaxCallDurationSec] = useState(600);
  const [interruptionSensitivity, setInterruptionSensitivity] = useState(0.5);

  // Configuration state
  const [companyName, setCompanyName]   = useState('');
  const [campaignId, setCampaignId]     = useState('');
  const [campaigns, setCampaigns]       = useState([]);

  // Dirty tracking — snapshot of last-saved values
  const [orig, setOrig] = useState({});

  const isDirty =
    voiceId                 !== orig.voiceId                 ||
    voiceSpeed              !== orig.voiceSpeed              ||
    voiceTemperature        !== orig.voiceTemperature        ||
    greeting                !== orig.greeting                ||
    endMessage              !== orig.endMessage              ||
    afterHoursEnabled       !== orig.afterHoursEnabled       ||
    afterHoursMessage       !== orig.afterHoursMessage       ||
    maxCallDurationSec      !== orig.maxCallDurationSec      ||
    interruptionSensitivity !== orig.interruptionSensitivity ||
    companyName             !== orig.companyName             ||
    campaignId              !== orig.campaignId;

  // ── Fetch voice config ───────────────────────────────────────────────────────
  const fetchVoiceConfig = useCallback(async () => {
    if (!user?.tenant_id) return;
    setLoading(true);
    setError('');
    try {
      const data = await makeAuthenticatedRequest('/voice/number');

      setVoiceAiEnabled(!!data.voice_ai_enabled);
      setVoiceCampaign(data.voice_campaign || null);
      setProvisionedAt(data.voice_ai_provisioned_at || null);

      const vId = data.voice_id || 'retell-Cimo';
      const isPreset = VOICE_PRESETS.some(v => v.id === vId);
      setVoiceId(vId);
      setCustomVoice(vId);
      setIsCustomVoice(!isPreset);
      setVoiceSpeed(parseFloat(data.voice_speed ?? 1.0));
      setVoiceTemperature(parseFloat(data.voice_temperature ?? 0.5));
      setGreeting(data.greeting || '');
      setEndMessage(data.end_message || '');

      setAfterHoursEnabled(!!data.after_hours_enabled);
      setAfterHoursMessage(data.after_hours_message || '');
      setMaxCallDurationSec(parseInt(data.max_call_duration_sec ?? 600, 10));
      setInterruptionSensitivity(parseFloat(data.interruption_sensitivity ?? 0.5));

      setCompanyName(data.voice_ai_company_name || '');
      setCampaignId(data.voice_ai_campaign_id   || '');

      setOrig({
        voiceId:                 vId,
        voiceSpeed:              parseFloat(data.voice_speed ?? 1.0),
        voiceTemperature:        parseFloat(data.voice_temperature ?? 0.5),
        greeting:                data.greeting    || '',
        endMessage:              data.end_message || '',
        afterHoursEnabled:       !!data.after_hours_enabled,
        afterHoursMessage:       data.after_hours_message || '',
        maxCallDurationSec:      parseInt(data.max_call_duration_sec ?? 600, 10),
        interruptionSensitivity: parseFloat(data.interruption_sensitivity ?? 0.5),
        companyName:             data.voice_ai_company_name || '',
        campaignId:              data.voice_ai_campaign_id  || '',
      });
    } catch (err) {
      setError(`Failed to load voice config: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [user?.tenant_id]);

  // ── Fetch campaigns ──────────────────────────────────────────────────────────
  // 🟢 2026-09-13: filters to allows_bulk_upload=false — a bulk-flagged
  // campaign can never become the voice channel (PATCH /settings/campaign
  // already refuses it server-side, CAMPAIGN_NOT_VOICE_ELIGIBLE); the picker
  // was offering them anyway and letting the save fail. Pre-existing bug,
  // fixed here since it directly affects this same picker.
  const fetchCampaigns = useCallback(async () => {
    if (!user?.tenant_id) return;
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .select('id, name')
        .eq('tenant_id', user.tenant_id)
        .eq('is_active', true)
        .eq('allows_bulk_upload', false)
        .order('name');
      if (error) throw error;
      setCampaigns(data || []);
    } catch (err) {
      console.error('Failed to fetch campaigns:', err.message);
    }
  }, [user?.tenant_id]);

  useEffect(() => {
    fetchVoiceConfig();
    fetchCampaigns();
  }, [fetchVoiceConfig, fetchCampaigns]);

  useEffect(() => {
    if (error || success) {
      const t = setTimeout(() => { setError(''); setSuccess(''); }, 5000);
      return () => clearTimeout(t);
    }
  }, [error, success]);

  // ── Save all settings ────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const effectiveVoiceId = isCustomVoice ? customVoice : voiceId;
      await Promise.all([
        makeAuthenticatedRequest('/voice/settings/voice', {
          method: 'PATCH',
          body: JSON.stringify({
            voice_id: effectiveVoiceId,
            voice_speed: voiceSpeed,
            voice_temperature: voiceTemperature,
          }),
        }),
        makeAuthenticatedRequest('/voice/settings/greeting', {
          method: 'PATCH',
          body: JSON.stringify({ greeting, end_message: endMessage }),
        }),
        makeAuthenticatedRequest('/voice/settings/behavior', {
          method: 'PATCH',
          body: JSON.stringify({
            max_call_duration_sec:    maxCallDurationSec,
            interruption_sensitivity: interruptionSensitivity,
            after_hours_enabled:      afterHoursEnabled,
            after_hours_message:      afterHoursMessage,
          }),
        }),
        makeAuthenticatedRequest('/voice/settings/name', {
          method: 'PATCH',
          body: JSON.stringify({ company_name: companyName }),
        }),
        makeAuthenticatedRequest('/voice/settings/campaign', {
          method: 'PATCH',
          body: JSON.stringify({ campaign_id: campaignId || null }),
        }),
      ]);

      setOrig({
        voiceId: effectiveVoiceId,
        voiceSpeed, voiceTemperature,
        greeting, endMessage,
        afterHoursEnabled, afterHoursMessage,
        maxCallDurationSec, interruptionSensitivity,
        companyName, campaignId,
      });
      setVoiceId(effectiveVoiceId);
      setSuccess('Settings saved!');
      // The "Default campaign for inbound calls" picker just above writes the
      // SAME tenants.voice_ai_campaign_id the Phone Number card's live-number
      // display reads — refetch so a campaign change shows up immediately
      // instead of only after the next full page load.
      if (campaignId !== orig.campaignId) {
        await fetchVoiceConfig();
      }
      onUpdate?.();
    } catch (err) {
      setError(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Set up Voice AI ──────────────────────────────────────────────────────────
  // Creates the tenant's one Retell agent. When a Voice campaign is already
  // designated AND already has its own number (required at creation —
  // VOICE_CAMPAIGN_NEEDS_NUMBER), reuses THAT number instead of purchasing a
  // second, tenant-level one — see voice.js's /number/provision header for why
  // (a real, avoidable $2/month for a number nobody would ever dial).
  const handleSetUpVoiceAi = async () => {
    setProvisionLoading(true);
    setError('');
    setSuccess('');
    try {
      const body = voiceCampaign?.phone_number_id
        ? { use_existing_phone_number_id: voiceCampaign.phone_number_id }
        : (areaCodeInput ? { area_code: areaCodeInput.trim() } : {});
      await makeAuthenticatedRequest('/voice/number/provision', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setSuccess('Voice AI is set up.');
      await fetchVoiceConfig();
      onUpdate?.();
    } catch (err) {
      setError(`Setup failed: ${err.message}`);
    } finally {
      setProvisionLoading(false);
    }
  };

  // ── Import the designated campaign's number ─────────────────────────────────
  // Covers the case where Voice AI is already provisioned and a Voice campaign
  // is designated, but its number was never imported into Retell — e.g. it was
  // assigned via the Configuration card's campaign picker (PATCH
  // /settings/campaign) rather than through Surf, which auto-imports at
  // designation time. Reuses the same manual retrofit route the old
  // per-campaign "Enable Voice Calling" card already used.
  const handleImportCampaignNumber = async () => {
    if (!voiceCampaign?.campaign_id) return;
    setImportLoading(true);
    setError('');
    setSuccess('');
    try {
      await makeAuthenticatedRequest('/voice/campaign-number/import', {
        method: 'POST',
        body: JSON.stringify({ campaign_id: voiceCampaign.campaign_id }),
      });
      setSuccess('Number connected to Retell.');
      await fetchVoiceConfig();
      onUpdate?.();
    } catch (err) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setImportLoading(false);
    }
  };

  // ── Swap Number ──────────────────────────────────────────────────────────────
  // Fetches this tenant's other active numbers, minus whichever ones are
  // already another campaign's own primary number (the server re-checks this
  // too — this is just so the picker doesn't offer a choice it would reject).
  const fetchInventoryNumbers = useCallback(async () => {
    if (!user?.tenant_id) return;
    try {
      const [{ data: numbers, error: numErr }, { data: claimed, error: claimErr }] = await Promise.all([
        supabase.from('phone_numbers')
          .select('id, phone_number')
          .eq('tenant_id', user.tenant_id)
          .eq('status', 'active')
          .order('phone_number'),
        supabase.from('campaigns')
          .select('phone_number_id')
          .eq('tenant_id', user.tenant_id)
          .not('phone_number_id', 'is', null),
      ]);
      if (numErr) throw numErr;
      if (claimErr) throw claimErr;
      const claimedIds = new Set((claimed || []).map(c => c.phone_number_id));
      const currentId = voiceCampaign?.phone_number_id;
      setInventoryNumbers((numbers || []).filter(n => n.id !== currentId && !claimedIds.has(n.id)));
    } catch (err) {
      console.error('Failed to fetch phone number inventory:', err.message);
    }
  }, [user?.tenant_id, voiceCampaign?.phone_number_id]);

  const openSwapModal = () => {
    setSwapError('');
    setSwapMode('existing');
    setSelectedInventoryId('');
    setAvailableNumbers([]);
    setSwapAreaCode('');
    fetchInventoryNumbers();
    setSwapModalOpen(true);
  };

  const handleSwapTo = async (newPhoneNumberId) => {
    setSwapping(true);
    setSwapError('');
    try {
      const data = await makeAuthenticatedRequest('/voice/number/swap', {
        method: 'POST',
        body: JSON.stringify({ new_phone_number_id: newPhoneNumberId }),
      });
      setSwapModalOpen(false);
      setSuccess(`Number swapped to ${formatPhone(data.phone_number)}.`);
      await fetchVoiceConfig();
      onUpdate?.();
    } catch (err) {
      setSwapError(`Swap failed: ${err.message}`);
    } finally {
      setSwapping(false);
    }
  };

  const handleSearchNumbers = async () => {
    const areaCode = swapAreaCode.trim();
    if (areaCode.length !== 3) return;
    setSearchingNumbers(true);
    setSwapError('');
    setAvailableNumbers([]);
    try {
      const { data, error } = await supabase.functions.invoke('phone_numbers', {
        body: { action: 'search', areaCode },
      });
      if (error) throw error;
      const found = data?.availableNumbers || [];
      if (found.length === 0) {
        setSwapError(`No phone numbers available in area code ${areaCode}. Try a different one.`);
      }
      setAvailableNumbers(found);
    } catch (err) {
      setSwapError(`Search failed: ${err.message}`);
    } finally {
      setSearchingNumbers(false);
    }
  };

  const handlePurchaseAndSwap = async (numberObj) => {
    setPurchasing(true);
    setSwapError('');
    try {
      const { data, error } = await supabase.functions.invoke('phone_numbers', {
        body: { action: 'purchase', phoneNumber: numberObj.phoneNumber },
      });
      if (error) throw error;
      if (!data?.success || !data?.phoneNumber?.id) {
        throw new Error(data?.message || 'Purchase failed');
      }
      await handleSwapTo(data.phoneNumber.id);
    } catch (err) {
      setSwapError(`Purchase failed: ${err.message}`);
    } finally {
      setPurchasing(false);
    }
  };

  // ── Voice preset click ──────────────────────────────────────────────────────
  const handleVoicePreset = (preset) => {
    if (preset.id === 'custom') {
      setIsCustomVoice(true);
      setVoiceId(customVoice || 'retell-Cimo');
    } else {
      setIsCustomVoice(false);
      setVoiceId(preset.id);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Banners */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <span className="text-sm text-emerald-700 font-medium">{success}</span>
        </div>
      )}

      {/* ── 1. Phone Number ── */}
      {(() => {
        const hasVoiceCampaign = !!voiceCampaign?.campaign_id;
        const isLive = voiceAiEnabled && !!phoneNumber; // phoneNumber is '' unless retell_imported_at is set

        return (
          <SectionCard
            icon={Phone}
            iconColor="from-cyan-500 to-blue-600"
            title="Phone Number"
            subtitle={
              isLive
                ? `Live on "${voiceCampaign.campaign_name}"`
                : hasVoiceCampaign
                  ? `Voice campaign "${voiceCampaign.campaign_name}" is designated — number not connected yet`
                  : voiceAiEnabled
                    ? 'Voice AI is set up — no dedicated line designated'
                    : 'Set up voice AI to enable inbound calls'
            }
          >
            {isLive ? (
              <div className="space-y-4">
                {/* Live number display */}
                <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-white border-2 border-cyan-300 flex items-center justify-center flex-shrink-0">
                    <PhoneCall className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Live inbound number</p>
                    <p className="text-lg font-mono font-semibold text-slate-900 truncate">{formatPhone(phoneNumber)}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Campaign: <span className="font-medium text-slate-500">{voiceCampaign.campaign_name}</span>
                      {provisionedAt && (
                        <> · Voice AI provisioned {new Date(provisionedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</>
                      )}
                    </p>
                  </div>
                  <CopyButton value={phoneNumber} />
                </div>

                {/* Test Call */}
                <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                  <Info className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 text-xs text-slate-600 leading-relaxed">
                    <strong className="text-slate-800">Test the live agent:</strong> dial <span className="font-mono">{formatPhone(phoneNumber)}</span> from your phone. The AI will answer with the greeting configured below, using "{voiceCampaign.campaign_name}" for context.
                  </div>
                  <a
                    href={`tel:${phoneNumber}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                               text-white bg-cyan-600 hover:bg-cyan-700 transition-colors flex-shrink-0"
                  >
                    <PhoneCall className="w-3.5 h-3.5" />
                    Test Call
                  </a>
                </div>

                {/* Swap Number */}
                <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="flex items-center space-x-3">
                    <Repeat className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">Swap Number</p>
                      <p className="text-xs text-slate-500">
                        Replace this campaign's number with a different one. The new number is connected and confirmed before the old one is let go — voice is never left without a number.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={openSwapModal}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                               text-slate-700 bg-white border border-slate-300 hover:bg-slate-100 transition-colors flex-shrink-0"
                  >
                    <Repeat className="w-3.5 h-3.5" />
                    Swap
                  </button>
                </div>
              </div>
            ) : hasVoiceCampaign ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    "{voiceCampaign.campaign_name}" is your designated Voice campaign
                    {voiceCampaign.phone_number ? (
                      <> and already has a number ({formatPhone(voiceCampaign.phone_number)}) — it just isn't connected to Retell yet.</>
                    ) : (
                      <>, but it has no phone number attached — attach one on the campaign itself before continuing.</>
                    )}
                  </p>
                </div>

                {voiceAiEnabled ? (
                  voiceCampaign.phone_number && (
                    <motion.button
                      onClick={handleImportCampaignNumber}
                      disabled={importLoading}
                      whileTap={{ scale: 0.98 }}
                      className={`
                        w-full px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200
                        flex items-center justify-center space-x-2 shadow-sm hover:shadow
                        bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:from-cyan-700 hover:to-blue-700
                        ${importLoading ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                    >
                      {importLoading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /><span>Connecting…</span></>
                      ) : (
                        <><PhoneCall className="w-4 h-4" /><span>Connect this number to Retell</span></>
                      )}
                    </motion.button>
                  )
                ) : (
                  <motion.button
                    onClick={handleSetUpVoiceAi}
                    disabled={provisionLoading || !voiceCampaign.phone_number}
                    whileTap={{ scale: 0.98 }}
                    className={`
                      w-full px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200
                      flex items-center justify-center space-x-2 shadow-sm hover:shadow
                      bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:from-cyan-700 hover:to-blue-700
                      ${(provisionLoading || !voiceCampaign.phone_number) ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    {provisionLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /><span>Setting up… (this takes ~10 seconds)</span></>
                    ) : (
                      <><Phone className="w-4 h-4" /><span>Set Up Voice AI</span></>
                    )}
                  </motion.button>
                )}
              </div>
            ) : voiceAiEnabled ? (
              <div className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                <Info className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-600">
                  Voice AI is provisioned for this tenant, but no dedicated inbound line is designated. Voice/greeting/behavior below still apply to any campaign using a Voice follow-up step. Pick a "Default campaign for inbound calls" below, or have Surf build a dedicated Voice campaign, to get a live number here.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Voice AI isn't set up yet. Click <strong>Set Up Voice AI</strong> below to purchase a Twilio number on your subaccount, wire it through the SurFox SIP trunk, and bind it to a new Retell agent.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Preferred area code <span className="text-slate-400 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={3}
                    value={areaCodeInput}
                    onChange={(e) => setAreaCodeInput(e.target.value.replace(/\D/g, ''))}
                    placeholder="e.g. 415"
                    className="w-32 px-3 py-2 border border-slate-200 rounded-xl text-sm font-mono
                               focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                  />
                  <p className="text-xs text-slate-400 mt-1.5">
                    Leave blank to search nationwide. Falls back to your account's preferred area code if set.
                  </p>
                </div>

                <motion.button
                  onClick={handleSetUpVoiceAi}
                  disabled={provisionLoading}
                  whileTap={{ scale: 0.98 }}
                  className={`
                    w-full px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200
                    flex items-center justify-center space-x-2 shadow-sm hover:shadow
                    bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:from-cyan-700 hover:to-blue-700
                    ${provisionLoading ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  {provisionLoading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /><span>Setting up… (this takes ~10 seconds)</span></>
                  ) : (
                    <><Phone className="w-4 h-4" /><span>Set Up Voice AI</span></>
                  )}
                </motion.button>
              </div>
            )}
          </SectionCard>
        );
      })()}

      {/* ── 2. Voice & Greeting ── */}
      <SectionCard
        icon={Mic}
        iconColor="from-violet-500 to-purple-600"
        title="Voice & Greeting"
        subtitle="How the AI sounds and what it says when answering"
      >
        <div className="space-y-6">

          {/* Voice picker */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Voice</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {VOICE_PRESETS.map((preset) => {
                const isSelected = !isCustomVoice && voiceId === preset.id;
                return (
                  <button
                    key={preset.id}
                    onClick={() => handleVoicePreset(preset)}
                    className={`p-3 rounded-xl border-2 text-sm font-medium transition-all text-left
                      ${isSelected
                        ? 'border-violet-500 bg-violet-50 text-violet-700'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{preset.label}</span>
                      {isSelected && <Check className="w-4 h-4 text-violet-600" />}
                    </div>
                    <code className="block mt-1 text-[11px] text-slate-400 font-mono">{preset.id}</code>
                  </button>
                );
              })}
              <button
                onClick={() => handleVoicePreset({ id: 'custom' })}
                className={`p-3 rounded-xl border-2 text-sm font-medium transition-all text-left
                  ${isCustomVoice
                    ? 'border-violet-500 bg-violet-50 text-violet-700'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <span>Custom voice ID</span>
                  {isCustomVoice && <Check className="w-4 h-4 text-violet-600" />}
                </div>
                <span className="block mt-1 text-[11px] text-slate-400">Any Retell or ElevenLabs voice id</span>
              </button>
            </div>

            {isCustomVoice && (
              <input
                type="text"
                value={customVoice}
                onChange={(e) => { setCustomVoice(e.target.value); setVoiceId(e.target.value); }}
                placeholder="e.g. eleven-labs-Rachel"
                className="mt-3 w-full px-3 py-2 border border-slate-200 rounded-xl text-sm font-mono
                           focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
              />
            )}
          </div>

          {/* Speed slider */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Speech speed <span className="text-slate-400 font-normal">({voiceSpeed.toFixed(2)}×)</span>
            </label>
            <input
              type="range"
              min={0.5}
              max={2.0}
              step={0.05}
              value={voiceSpeed}
              onChange={(e) => setVoiceSpeed(parseFloat(e.target.value))}
              className="w-full accent-violet-500"
            />
            <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
              <span>0.5× (slow)</span>
              <span>1.0× (natural)</span>
              <span>2.0× (fast)</span>
            </div>
          </div>

          {/* Temperature slider */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Voice temperature <span className="text-slate-400 font-normal">({voiceTemperature.toFixed(2)})</span>
            </label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={voiceTemperature}
              onChange={(e) => setVoiceTemperature(parseFloat(e.target.value))}
              className="w-full accent-violet-500"
            />
            <p className="text-xs text-slate-400 mt-0.5">
              Higher = more expressive variance turn-to-turn. Lower = more consistent monotone.
            </p>
          </div>

          {/* Greeting */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Opening greeting</label>
            <textarea
              value={greeting}
              onChange={(e) => setGreeting(e.target.value)}
              rows={2}
              maxLength={300}
              placeholder="Hey, thanks for calling SurFox! How can I help?"
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm
                         focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
            />
            <p className="text-xs text-slate-400 mt-1.5">
              Spoken aloud the moment a call connects. Supports <code className="bg-slate-100 px-1 rounded">{'{first_name}'}</code> and <code className="bg-slate-100 px-1 rounded">{'{company_name}'}</code>.
            </p>
          </div>

          {/* End message */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">End-of-call message</label>
            <textarea
              value={endMessage}
              onChange={(e) => setEndMessage(e.target.value)}
              rows={2}
              maxLength={300}
              placeholder="Thanks for calling — we'll be in touch soon!"
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm
                         focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
            />
          </div>
        </div>
      </SectionCard>

      {/* ── 3. Behavior ── */}
      <SectionCard
        icon={Zap}
        iconColor="from-amber-500 to-orange-500"
        title="Call Behavior"
        subtitle="How the agent paces itself and handles edge cases"
      >
        <div className="space-y-5">

          {/* Max call duration */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Max call length <span className="text-slate-400 font-normal">({Math.floor(maxCallDurationSec / 60)} min {maxCallDurationSec % 60}s)</span>
            </label>
            <input
              type="range"
              min={60}
              max={1800}
              step={30}
              value={maxCallDurationSec}
              onChange={(e) => setMaxCallDurationSec(parseInt(e.target.value, 10))}
              className="w-full accent-orange-500"
            />
            <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
              <span>1 min</span>
              <span>15 min</span>
              <span>30 min</span>
            </div>
            <p className="text-xs text-slate-400 mt-1.5">
              Hard ceiling — the agent ends the call gracefully when reached.
            </p>
          </div>

          {/* Interruption sensitivity */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Interruption sensitivity <span className="text-slate-400 font-normal">({interruptionSensitivity.toFixed(2)})</span>
            </label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={interruptionSensitivity}
              onChange={(e) => setInterruptionSensitivity(parseFloat(e.target.value))}
              className="w-full accent-orange-500"
            />
            <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
              <span>0 (patient)</span>
              <span>1 (alert)</span>
            </div>
            <p className="text-xs text-slate-400 mt-1.5">
              Higher = stops talking the moment the caller starts. Lower = finishes the sentence first.
            </p>
          </div>

          {/* After hours */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between p-4 bg-slate-50">
              <div>
                <p className="font-medium text-slate-900 text-sm">After-hours fallback</p>
                <p className="text-xs text-slate-500 mt-0.5">Play a message and end the call outside business hours instead of running the agent</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer flex-shrink-0 ml-4">
                <span className="text-xs text-slate-500">{afterHoursEnabled ? 'On' : 'Off'}</span>
                <div className="relative" onClick={() => setAfterHoursEnabled(v => !v)}>
                  <div className={`w-10 h-6 rounded-full transition-colors ${afterHoursEnabled ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${afterHoursEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                  </div>
                </div>
              </label>
            </div>
            {afterHoursEnabled && (
              <div className="px-4 pb-4 pt-3 border-t border-slate-100 bg-white">
                <label className="block text-xs font-medium text-slate-600 mb-1.5">After-hours message</label>
                <textarea
                  value={afterHoursMessage}
                  onChange={(e) => setAfterHoursMessage(e.target.value)}
                  rows={2}
                  maxLength={300}
                  placeholder="Thanks for calling — our team is offline right now. Please call back during business hours, or leave us a message at..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm
                             focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                />
              </div>
            )}
          </div>

          {/* Info callout */}
          <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-100 rounded-xl">
            <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-700 leading-relaxed">
              Business hours come from your office days setting. After-hours behavior only fires when there's no live agent path configured for the time window.
            </p>
          </div>
        </div>
      </SectionCard>

      {/* ── 4. Configuration ── */}
      <SectionCard
        icon={Globe}
        iconColor="from-emerald-500 to-teal-600"
        title="Configuration"
        subtitle="Greeting context and lead routing"
      >
        <div className="space-y-5">
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Company display name</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="e.g. Acme Staffing"
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm
                         focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            />
            <p className="text-xs text-slate-400 mt-1.5">
              Substituted into the agent prompt via <code className="bg-slate-100 px-1 rounded">{'{company_name}'}</code>. The default greeting reads:
              "Hey, thanks for calling <em>{companyName || 'your company'}</em>!"
            </p>
          </div>

          {/* Campaign */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Default campaign for inbound calls</label>
            <select
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-white
                         focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            >
              <option value="">— No campaign (unassigned) —</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1.5">
              New leads created from inbound calls (matched by caller number) are enrolled in this campaign automatically.
            </p>
          </div>
        </div>
      </SectionCard>

      {/* Save */}
      {isDirty && (
        <div className="flex items-center justify-end pt-2">
          <motion.button
            onClick={handleSave}
            disabled={saving}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            whileTap={{ scale: 0.98 }}
            className={`
              px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200
              flex items-center space-x-2 shadow-sm hover:shadow
              ${success ? 'bg-emerald-500 text-white' : 'bg-slate-900 text-white hover:bg-slate-800'}
              ${saving ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 animate-spin" /><span>Saving…</span></>
            ) : success ? (
              <><Check className="w-4 h-4" /><span>Saved</span></>
            ) : (
              <span>Save Changes</span>
            )}
          </motion.button>
        </div>
      )}

      {/* ── Swap Number modal ── */}
      <AnimatePresence>
        {swapModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
            onClick={() => !swapping && !purchasing && setSwapModalOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between p-5 border-b border-slate-100">
                <div>
                  <h3 className="font-semibold text-slate-900">Swap Number</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Replacing {formatPhone(phoneNumber)} on "{voiceCampaign?.campaign_name}"
                  </p>
                </div>
                <button
                  onClick={() => setSwapModalOpen(false)}
                  disabled={swapping || purchasing}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-5 space-y-4">
                {swapError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-red-700">{swapError}</span>
                  </div>
                )}

                {/* Mode tabs */}
                <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
                  <button
                    onClick={() => setSwapMode('existing')}
                    className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors
                      ${swapMode === 'existing' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    Use existing number
                  </button>
                  <button
                    onClick={() => setSwapMode('search')}
                    className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors
                      ${swapMode === 'search' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    Buy a new number
                  </button>
                </div>

                {swapMode === 'existing' ? (
                  <div className="space-y-3">
                    {inventoryNumbers.length === 0 ? (
                      <p className="text-sm text-slate-500 py-4 text-center">
                        No other unused numbers in your inventory. Try "Buy a new number" instead.
                      </p>
                    ) : (
                      <>
                        <select
                          value={selectedInventoryId}
                          onChange={(e) => setSelectedInventoryId(e.target.value)}
                          className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm bg-white
                                     focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                        >
                          <option value="">Select a number…</option>
                          {inventoryNumbers.map((n) => (
                            <option key={n.id} value={n.id}>{formatPhone(n.phone_number)}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => selectedInventoryId && handleSwapTo(Number(selectedInventoryId))}
                          disabled={!selectedInventoryId || swapping}
                          className={`w-full px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors
                            bg-slate-900 text-white hover:bg-slate-800
                            ${(!selectedInventoryId || swapping) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {swapping ? (
                            <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Swapping…</span>
                          ) : 'Swap to this number'}
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={3}
                        value={swapAreaCode}
                        onChange={(e) => setSwapAreaCode(e.target.value.replace(/\D/g, ''))}
                        placeholder="Area code, e.g. 415"
                        className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm font-mono
                                   focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                      />
                      <button
                        onClick={handleSearchNumbers}
                        disabled={swapAreaCode.length !== 3 || searchingNumbers}
                        className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-1.5
                          bg-slate-900 text-white hover:bg-slate-800
                          ${(swapAreaCode.length !== 3 || searchingNumbers) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {searchingNumbers ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                        Search
                      </button>
                    </div>

                    {availableNumbers.length > 0 && (
                      <div className="space-y-1.5 max-h-56 overflow-y-auto">
                        {availableNumbers.map((n) => (
                          <div key={n.phoneNumber} className="flex items-center justify-between p-2.5 border border-slate-200 rounded-lg">
                            <span className="text-sm font-mono text-slate-700">{formatPhone(n.phoneNumber)}</span>
                            <button
                              onClick={() => handlePurchaseAndSwap(n)}
                              disabled={purchasing}
                              className={`px-3 py-1 text-xs font-medium rounded-lg text-white bg-cyan-600 hover:bg-cyan-700
                                ${purchasing ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {purchasing ? 'Buying…' : 'Buy & swap'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
